import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { SnackbarService, SnackbarStatus } from '@prism/angular-shared';
import { AppwriteApiEndpoint, AppwriteApiRoute } from '@prism/prism-shared';
import { BehaviorSubject, catchError, delay, EMPTY, first, from, interval, map, mergeMap, Observable, of, startWith, Subject, switchMap, takeUntil, tap, throwError, timer, withLatestFrom } from 'rxjs';
import { AppwriteAuthService } from '../shared/services/appwrite-auth.service';
import { ScreenService } from '../shared/services/screen.service';
import { UserApiService } from '../shared/services/user-api.service';
import { CoreActions, CoreSelectors } from '../shared/state/core';
import { environment } from '../../environments/environment';
import { EnvironmentId, PrismModule, SettingsSubModule, User } from '@prism/prism-shared';
import { DataserverApiService } from '../shared/services/dataserver-api.service';

@Component({
  selector: 'prism-client-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {

  public formGroup: FormGroup<{
    email: FormControl<string | null>;
    password: FormControl<string | null>;
  }> = new FormGroup({
    email: new FormControl<string | null>(null, [Validators.required, Validators.email]),
    password: new FormControl<string | null>(null, [Validators.required]),
  })


  public showPassword$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public submitEnabled$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loginInProgress$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public loginSuccessful$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public passwordRecoveryMode$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private unsubscribe$: Subject<void> = new Subject();
  private recoveryUserId: string | null = null;
  private recoverySecret: string | null = null;

  constructor(
    private store: Store,
    private router: Router,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private screenService: ScreenService,
    private userApiService: UserApiService,
    private dataserverApi: DataserverApiService,
    private auth: AppwriteAuthService,
    private http: HttpClient,
  ) { }

  ngOnInit() {
    from(this.auth.signOut()).pipe(first()).subscribe();
    this.store.dispatch(CoreActions.ClearCurrentUser());
    this.screenService.hideMainHeader$.next(true);

    this.recoveryUserId = this.route.snapshot.queryParamMap.get('userId');
    this.recoverySecret = this.route.snapshot.queryParamMap.get('secret');
    const isPasswordRecovery = !!this.recoveryUserId && !!this.recoverySecret;
    this.passwordRecoveryMode$.next(isPasswordRecovery);
    if (isPasswordRecovery) {
      this.formGroup.controls.email.clearValidators();
      this.formGroup.controls.email.updateValueAndValidity();
    }

    this.formGroup.valueChanges.pipe(
      tap((v) => {
        this.submitEnabled$.next(this.formGroup.valid)
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe()


    const fiveMinutes = 5 * 60 * 1000

    timer(fiveMinutes).pipe(
      tap(() => window.location.reload()),
      takeUntil(this.unsubscribe$)
    ).subscribe()

  }

  ngOnDestroy() {
    this.screenService.hideMainHeader$.next(false);
    this.unsubscribe$.next(),
      this.unsubscribe$.complete()
  }

  public login() {
    if (this.passwordRecoveryMode$.value) {
      this.completePasswordRecovery();
      return;
    }


    if (this.formGroup.valid) {

      this.loginInProgress$.next(true);


      const email = this.formGroup.controls.email.value || "";
      const password = this.formGroup.controls.password.value || "";


      from(this.auth.signInWithEmailAndPassword(email, password)).pipe(
        first(),
        delay(2000),
        tap((credential) => {
          this.userApiService.getUserById(credential.user?.uid || "").pipe(
            switchMap((user) => {
              if (!user) {
                return throwError(() => '')
              }

              return this.checkUserOnSite(user).pipe(
                mergeMap((onsite) => {

                  this.loginInProgress$.next(false);
                  this.loginSuccessful$.next(true);

                  if (!onsite) {
                    return from(this.auth.signOut()).pipe(
                      tap(() => {
                        this.router.navigate(["/no-access"])
                      }),
                      catchError(() => {
                        return of(null);
                      })
                    )
                  }

                  this.store.dispatch(CoreActions.LoadCurrentUserSuccess({ user }))

                  return from(this.router.navigate(["/"])).pipe(
                    tap(() => {
                      this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, 'welcome!')
                    }),
                    catchError((error) => {
                      this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'error logging in')
                      return EMPTY;
                    })
                  )
                })
              )

            }),
            catchError(() => {
              this.loginInProgress$.next(false);
              this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'error logging in')
              return of();
            })
          ).subscribe()
        }),
        catchError((error) => {
          this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'error logging in')
          this.loginInProgress$.next(false);
          this.formGroup.controls.password.setValue('')
          return from([])
        }),
        takeUntil(this.unsubscribe$)
      ).subscribe()

    }
  }

  private completePasswordRecovery() {
    const password = this.formGroup.controls.password.value || '';
    if (!this.recoveryUserId || !this.recoverySecret || !password) {
      this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'password reset link is invalid')
      return;
    }

    this.loginInProgress$.next(true);
    from(this.auth.completePasswordRecovery(this.recoveryUserId, this.recoverySecret, password)).pipe(
      first(),
      tap(() => {
        this.loginInProgress$.next(false);
        this.loginSuccessful$.next(true);
        this.snackbarService.showSnackbar(SnackbarStatus.SUCCESS, 'password reset')
        this.router.navigate(['/login']);
      }),
      catchError(() => {
        this.loginInProgress$.next(false);
        this.snackbarService.showSnackbar(SnackbarStatus.ERROR, 'error resetting password')
        return EMPTY;
      }),
      takeUntil(this.unsubscribe$)
    ).subscribe();
  }

  public navigateToBadgeLoginPage() {
    this.router.navigate(['/badge-login'])
  }

  public toggleShowPassword() {
    this.showPassword$.next(!this.showPassword$.value)
  }

  public checkUserOnSite(currentUser: User): Observable<boolean> {
    if (environment.environmentId !== EnvironmentId.Production) {
      return of(true);
    }

    return this.dataserverApi.ping().pipe(
      first(),
      map(() => {
        console.log('user on prism network.')
        return true;
      }),
      catchError(() => {
        console.log('user NOT on prism network.')
        return this.userApiService.checkUserOnNetwork().pipe(
          first(),
          map(({ onNetwork }) => {
            if (onNetwork) {
              console.log('user on network.')
              return true;
            }
            const canViewOffNetwork = currentUser.abilities[PrismModule.Settings][SettingsSubModule.Display].CanViewOffNetwork;
            if (canViewOffNetwork) {
              console.log('user can view app off network.')
              return true;
            }
            return false;
          })
        )
      })
    )
  }

}
